import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { DataService } from '../../services/data.service';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment.stage';

@Component({
    selector: 'app-nhs-button',
    templateUrl: 'nhs-button.component.html',
    styleUrls: ['nhs-button.component.scss'],
    imports: [
        CommonModule,
        TranslateModule,
    ]
})
export class NhsButtonComponent implements OnInit {
    @Input() label: string = '';
    @Input() disabled: boolean = false;
    @Input() showLogo: boolean = true;
    @Output() nhsClick = new EventEmitter<void>();
    @Input() action?: any | (() => void);

    // New inputs
    @Input() nhsQuery: string = '';      // prebuilt query fragment like "id=xxx"
    @Input() addQueryString?: string;    // extra query string to append

    constructor(public data: DataService, private translate: TranslateService) { }

    onClick() {
        if (!this.disabled) {
            this.nhsClick.emit();
            if (this.action) {
                if (typeof this.action === 'function') {
                    this.action();
                } else {
                    switch (this.action) {
                        case 'settings':
                            this.openNhsSettings();
                            break;
                        case 'signin':
                        default:
                            this.signIn();
                            break;
                    }
                }
            } else {
                this.signIn();
            }
        }
    }

    ngOnInit() {
        if (!this.label) {
            this.label = this.translate.instant('login.continue.to.nhs.login');
        }
    }

    async openNhsSettings() {
        // Open the NHS settings URL from environment in a new tab/window
        const platform = this.data?.diag?.platform ?? null;
        const apiUrl = environment.nhsSettingsUrl;

        try {
            if (platform === null || (platform !== 'ios' && platform !== 'android')) {
                // Web: navigate the top window to the API login endpoint
                window.top.location.href = apiUrl;
            } else {
                // Mobile: open using Capacitor Browser via dynamic import to avoid build-time dependency
                const mod = await import('@capacitor/browser');
                if (mod && typeof mod.Browser?.open === 'function') {
                    await mod.Browser.open({ url: apiUrl });
                } else {
                    // Fallback to window navigation if dynamic import not available
                    window.location.href = apiUrl;
                }
            }
        } catch (err) {
            // On any error, fallback to a simple location change
            try { window.location.href = apiUrl; } catch (e) { /* swallow */ }
        }
    }

    // New method: performs default NHS sign-in flow when no action is provided
    private async signIn() {
        // Platform-aware redirect handling using dataService
        const base = this.data?.basePath || '';
        const platform = this.data?.diag?.platform ?? null;
        const callbackUrl = platform === null
            ? `${base}nhs-callback`
            : (platform === 'android' ? 'app://com.getubetter.gubapp/nhs-callback' : 'gub://nhs-callback');

        // Build query: if nhsQuery provided use it, otherwise default to page=login (like login.page.ts)
        const parts: string[] = [];
        if (this.nhsQuery && this.nhsQuery.trim().length) {
            parts.push(this.nhsQuery.replace(/^\?/, ''));
        } else {
            parts.push('page=login');
        }

        // Append any additional query string provided (only if present)
        if (this.addQueryString && this.addQueryString.trim().length) {
            parts.push(this.addQueryString.replace(/^\?/, ''));
        }

        const query = parts.join('&');
        const apiUrl = `${base}api/nhs/login?${query ? (query + '&') : ''}redirect_url=${encodeURIComponent(callbackUrl)}`;

        try {
            if (platform === null || (platform !== 'ios' && platform !== 'android')) {
                // Web: navigate the top window to the API login endpoint
                window.top.location.href = apiUrl;
            } else {
                // Mobile: open using Capacitor Browser via dynamic import to avoid build-time dependency
                const mod = await import('@capacitor/browser');
                if (mod && typeof mod.Browser?.open === 'function') {
                    await mod.Browser.open({ url: apiUrl });
                } else {
                    // Fallback to window navigation if dynamic import not available
                    window.location.href = apiUrl;
                }
            }
        } catch (err) {
            // On any error, fallback to a simple location change
            try { window.location.href = apiUrl; } catch (e) { /* swallow */ }
        }
    }

}